// src/pages/HomePage.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./HomePage.module.css";
import { AuthContext } from "../auth/AuthProvider";
import { AUTH_MODES, getAuthMode } from "../auth/authConfig";
import { registerUser } from "../api/healthThreatsApi";

export default function HomePage() {
  const navigate = useNavigate();
  const { login, register, authenticated, ready, mode } = useContext(AuthContext);

  const authMode = useMemo(() => mode || getAuthMode(), [mode]);
  const isBypass = authMode === AUTH_MODES.BYPASS;
  const isDemo = authMode === AUTH_MODES.DEMO;
  const isKeycloak = !isBypass && !isDemo;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDemoFields, setShowDemoFields] = useState(false);

  // Keycloak self-registration (custom form, role dropdown)
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [regUsername, setRegUsername] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regRole, setRegRole] = useState("viewer");
  const [regError, setRegError] = useState("");
  const [regSuccess, setRegSuccess] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  useEffect(() => {
    if (ready && authenticated) {
      navigate("/landing", { replace: true });
    }
  }, [ready, authenticated, navigate]);

  const handleLogin = async (event) => {
    event?.preventDefault();
    if (isDemo && !showDemoFields) {
      setError("");
      setShowDemoFields(true);
      return;
    }
    setError("");
    setIsSubmitting(true);

    try {
      const result = await login(
        isDemo
          ? {
              username,
              password,
            }
          : undefined
      );

      if (result?.ok === false) {
        setError(result.error || "Login failed.");
        return;
      }

      if (isBypass || isDemo) {
        navigate("/landing", { replace: true });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async () => {
    if (isDemo) return;

    if (isKeycloak) {
      setRegError("");
      setRegSuccess(false);
      setShowRegisterForm(true);
      return;
    }

    const result = await register();

    if (result?.ok === false) {
      setError(result.error || "Registration failed.");
      return;
    }

    if (isBypass) {
      navigate("/landing", { replace: true });
    }
  };

  const handleRegisterSubmit = async (event) => {
    event?.preventDefault();
    setRegError("");

    if (regPassword.length < 8) {
      setRegError("Password must be at least 8 characters.");
      return;
    }

    setIsRegistering(true);

    try {
      await registerUser({
        username: regUsername,
        email: regEmail,
        password: regPassword,
        role: regRole,
      });
      setRegSuccess(true);
    } catch (err) {
      setRegError(err?.message || "Registration failed.");
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <main className={styles.wrapper} data-page="home">
      <div className={styles.center}>
        <img
          src="/ESCORT_logo.svg"
          alt="ESCORT"
          className={styles.logo}
          width={360}
          height={90}
        />

        <section className={styles.card} aria-label="Authentication">
          <h1 className={styles.title}>Knowledge Repository</h1>

          {isBypass && (
            <div className={styles.bypass}>AUTH BYPASSED (DEV MODE)</div>
          )}

          {isDemo ? (
            <form className={styles.form} onSubmit={handleLogin} noValidate>
              <div className={styles.actions}>
                <button
                  type="submit"
                  className={styles.button}
                  disabled={!ready || isSubmitting}
                >
                  Login
                </button>

                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonDisabled}`}
                  disabled
                  aria-disabled="true"
                  title="Registration is disabled for this demonstration."
                >
                  Register
                </button>
              </div>

              {showDemoFields && (
                <div className={styles.expandedFields}>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="username">
                      Username
                    </label>
                    <input
                      id="username"
                      className={styles.input}
                      type="text"
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                      autoComplete="username"
                      placeholder="Enter username"
                      disabled={!ready || isSubmitting}
                      autoFocus
                    />
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="password">
                      Password
                    </label>
                    <input
                      id="password"
                      className={styles.input}
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      autoComplete="current-password"
                      placeholder="Enter password"
                      disabled={!ready || isSubmitting}
                    />
                  </div>
                </div>
              )}

              {error && <div className={styles.error}>{error}</div>}
            </form>
          ) : isKeycloak && showRegisterForm ? (
            regSuccess ? (
              <div className={styles.actions}>
                <div className={styles.error} style={{ color: "var(--ui-success, #1f7a3d)", background: "var(--ui-success-bg, rgba(34,197,94,0.12))", borderColor: "var(--ui-border, rgba(34,197,94,0.28))" }}>
                  Account created! You can now log in.
                </div>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => {
                    setShowRegisterForm(false);
                    setRegSuccess(false);
                    setRegUsername("");
                    setRegEmail("");
                    setRegPassword("");
                    setRegRole("viewer");
                  }}
                >
                  Back to login
                </button>
              </div>
            ) : (
              <form className={styles.form} onSubmit={handleRegisterSubmit} noValidate>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="reg-username">
                    Username
                  </label>
                  <input
                    id="reg-username"
                    className={styles.input}
                    type="text"
                    value={regUsername}
                    onChange={(event) => setRegUsername(event.target.value)}
                    autoComplete="username"
                    placeholder="Choose a username"
                    disabled={isRegistering}
                    autoFocus
                    required
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="reg-email">
                    Email
                  </label>
                  <input
                    id="reg-email"
                    className={styles.input}
                    type="email"
                    value={regEmail}
                    onChange={(event) => setRegEmail(event.target.value)}
                    autoComplete="email"
                    placeholder="you@example.com"
                    disabled={isRegistering}
                    required
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="reg-password">
                    Password
                  </label>
                  <input
                    id="reg-password"
                    className={styles.input}
                    type="password"
                    value={regPassword}
                    onChange={(event) => setRegPassword(event.target.value)}
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    minLength={8}
                    disabled={isRegistering}
                    required
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="reg-role">
                    Account type
                  </label>
                  <select
                    id="reg-role"
                    className={styles.input}
                    value={regRole}
                    onChange={(event) => setRegRole(event.target.value)}
                    disabled={isRegistering}
                  >
                    <option value="viewer">Viewer — standard access</option>
                    <option value="contributor">Contributor — can also upload datasets</option>
                  </select>
                </div>

                {regError && <div className={styles.error}>{regError}</div>}

                <div className={styles.actions}>
                  <button
                    type="submit"
                    className={styles.button}
                    disabled={isRegistering}
                  >
                    {isRegistering ? "Creating account..." : "Create account"}
                  </button>

                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => {
                      setShowRegisterForm(false);
                      setRegError("");
                    }}
                    disabled={isRegistering}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )
          ) : (
            <div className={styles.actions}>
              {error && <div className={styles.error}>{error}</div>}

              <button
                type="button"
                className={styles.button}
                onClick={handleLogin}
                disabled={!ready || isSubmitting}
              >
                Login
              </button>

              <button
                type="button"
                className={styles.button}
                onClick={handleRegister}
                disabled={!ready || isSubmitting}
              >
                Register
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
